// src/prototype/pages/EAPrintableAssumptionsAppendixPage.tsx
// Assumptions Appendix — Financial Model Inputs & Benchmarks
// Final artifact in the EA deliverable set.
// CI Pass: Model Context panel added, Model Overview tightened,
// benchmark and cost intro copy clarified. Shell, route, and
// print framework unchanged.

import React from 'react';
import { DEMO_CLIENT } from '../config/demoClient';
import { demoMonthYear } from '../config/demoDates';
import {
  EAUseCaseArtifactShell,
  type SummaryMetric,
  type ArtifactSection,
} from '../components/ea_reports/use_case/EAUseCaseArtifactShell';
import {
  CANON,
  CANON_CONFIDENCE_BASIS,
  CARE_COORD_DISCLOSURE,
  CARE_COORD_ROI,
  CARE_COORD_VALUE_CHAIN,
  ROI_CONFIG,
} from '../roi/roiCanon';
import {
  bandHighOf,
  bandLowOf,
  formatDriverValue,
  formatUsd,
  isScenarioVaried,
} from '../roi/roiEngine';

// ─── Summary Strip ────────────────────────────────────────────────────────────
//
// This document is the model's input register, so it is generated FROM the driver
// set rather than authored alongside it. Two things it previously got wrong:
// the itemised cost lines summed to ~$0.95M against a stated $2.1M–$3.4M total,
// and the "auditable directly from these inputs" chain produced a low end ~3.5%
// away from the figure it published.

const SUMMARY_METRICS: SummaryMetric[] = [
  { label: 'Model Basis',              value: 'Care Coordination ROI Model',    sub: 'financial model foundation'    },
  { label: 'Estimated Annual Value',   value: CANON.annualValueRange, accent: true, sub: 'conservative–aggressive span' },
  { label: 'Confidence Level',         value: CANON.confidencePct,    accent: true, sub: 'model confidence score'     },
  { label: 'Data Sources',             value: 'Internal + Benchmark',           sub: 'validated inputs'              },
  { label: 'Recommendation Context',   value: 'First-Wave Priority',           sub: 'portfolio classification'     },
];

// ─── Shared Table Styles ──────────────────────────────────────────────────────

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '8.5pt',
  marginTop: '6px',
};

const thStyle: React.CSSProperties = {
  padding: '7px 12px',
  textAlign: 'left',
  background: '#18365E',
  color: '#ffffff',
  fontWeight: 700,
  fontSize: '7pt',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
};

const tdStyle: React.CSSProperties = {
  padding: '8px 12px',
  color: '#374151',
  verticalAlign: 'top',
  borderBottom: '1px solid #e5e7eb',
  lineHeight: 1.5,
};

const tdLabelStyle: React.CSSProperties = {
  ...tdStyle,
  fontWeight: 600,
  color: '#18365E',
  width: '38%',
};

const rowEven: React.CSSProperties = { background: '#ffffff' };
const rowOdd:  React.CSSProperties = { background: '#F5F7FA' };

// ─── Assumption Tables ────────────────────────────────────────────────────────

/** One table row per model input, with its scenario spread or its tested band. */
function driverRows(roles: readonly string[]) {
  return CARE_COORD_ROI.drivers
    .filter((d) => roles.includes(d.role))
    .map((d) => ({
      assumption: d.name.replace(/\s*\([^)]*\)\s*$/, ''),
      value: isScenarioVaried(d)
        ? `${formatDriverValue(d, d.conservative)} – ${formatDriverValue(d, d.aggressive)} (base ${formatDriverValue(d, d.base)})`
        : `${formatDriverValue(d, d.base)} — canon in all scenarios, tested ${formatDriverValue(d, bandLowOf(d))} – ${formatDriverValue(d, bandHighOf(d))}`,
      rationale: `${d.description ?? d.name} · ${d.source} (${d.sourceType}, ${d.confidence} confidence)`,
    }));
}

const REVENUE_ASSUMPTIONS = [
  { assumption: 'Target patient cohort',           value: 'High-risk inpatient discharge population',  rationale: 'Patients with ≥2 chronic conditions or prior 30-day readmission history' },
  ...driverRows(['valueMultiplier', 'valueAddend']),
];

const COST_ASSUMPTIONS = [
  ...driverRows(['implementationCost', 'operatingCost', 'ramp']),
  {
    assumption: `Total ${ROI_CONFIG.horizonYears}-year cost`,
    value: CANON.totalCost3Yr,
    rationale: `Derived, not stated: ${CANON.implementationCost} one-time implementation plus ${CANON.operatingCostPerYear} operating in each of ${ROI_CONFIG.horizonYears} years. This is the denominator of the return figure.`,
  },
];

const BENCHMARK_INPUTS = [
  { source: 'NEJM Catalyst — Transitions of Care',         input: 'Readmission rate reduction benchmarks for structured post-discharge programs' },
  { source: 'Advisory Board Care Coordination Study',      input: 'FTE staffing ratios and care management capacity models for high-risk populations' },
  { source: 'CMS Hospital Readmissions Reduction Program', input: 'Penalty exposure estimates and quality metric improvement targets' },
  { source: 'Internal Operational Baseline (Sample)',      input: 'Representative readmission rates, discharge volumes, and care management capacity data' },
  { source: 'Payer Contract Analysis',                     input: 'Shared savings terms, quality bonus thresholds, and bundled payment exposure' },
];

const AssumptionTable: React.FC<{
  rows: { assumption: string; value: string; rationale: string }[];
}> = ({ rows }) => (
  <table style={tableStyle}>
    <thead>
      <tr>
        <th style={{ ...thStyle, width: '30%' }}>Assumption</th>
        <th style={{ ...thStyle, width: '25%' }}>Value</th>
        <th style={{ ...thStyle, width: '45%' }}>Basis / Rationale</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((row, i) => (
        <tr key={i} style={i % 2 === 0 ? rowEven : rowOdd}>
          <td style={tdLabelStyle}>{row.assumption}</td>
          <td style={{ ...tdStyle, fontWeight: 600, color: '#FF6E4C' }}>{row.value}</td>
          <td style={tdStyle}>{row.rationale}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const BenchmarkTable: React.FC = () => (
  <table style={tableStyle}>
    <thead>
      <tr>
        <th style={{ ...thStyle, width: '40%' }}>Source</th>
        <th style={{ ...thStyle, width: '60%' }}>Input Used</th>
      </tr>
    </thead>
    <tbody>
      {BENCHMARK_INPUTS.map((row, i) => (
        <tr key={i} style={i % 2 === 0 ? rowEven : rowOdd}>
          <td style={tdLabelStyle}>{row.source}</td>
          <td style={tdStyle}>{row.input}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

// ─── Model Context Panel ──────────────────────────────────────────────────────

const ModelContext: React.FC = () => (
  <div style={{
    border: '1.5px solid #18365E',
    borderLeft: '5px solid #FF6E4C',
    borderRadius: '6px',
    background: '#F0F4F9',
    padding: '14px 18px',
  }}>
    <div style={{
      fontSize: '7pt',
      fontWeight: 700,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: '#FF6E4C',
      marginBottom: '6px',
    }}>
      Model Context
    </div>
    <p style={{ fontSize: '9.5pt', color: '#18365E', lineHeight: 1.6, margin: 0, fontWeight: 500 }}>
      The following assumptions define the financial and operational inputs used to estimate
      the projected ROI for Care Coordination and Transition Management. These assumptions
      were derived from health system operational baselines, industry benchmarks, and
      reviewed analytics models.
    </p>
  </div>
);

// ─── Sections ─────────────────────────────────────────────────────────────────

const SECTIONS: ArtifactSection[] = [
  {
    title: '',
    content: <ModelContext />,
  },
  {
    title: 'Model Overview',
    content: (
      <p>
        This appendix documents the financial and operational inputs underlying the ROI
        model for Care Coordination and Transition Management. It is intended to support
        leadership validation of the financial outputs presented in the CFO ROI Summary
        and Board Rationale. All values reflect base case assumptions derived from a
        combination of representative health system operational baselines and confirmed external
        benchmarks, expressed in normalized format.
        These inputs support the projected annual value of <strong>{CANON.annualValueRange}</strong>{' '}
        and the investment of <strong>{CANON.investmentSchedule}</strong> —{' '}
        <strong>{CANON.totalCostLabel}</strong> — presented across the CFO ROI Summary, Board
        Rationale, and Use Case Profile. Every one of those figures is computed from the tables
        below by a single financial model; the definitions it uses are listed in the Calculation
        Method section.
      </p>
    ),
  },
  {
    title: 'Revenue Impact Assumptions',
    content: (
      <>
        <p>
          The following inputs drive the estimated annual value range of{' '}
          <strong>{CANON.annualValueRange}</strong>. Primary value is generated through reduction
          of avoidable readmissions and improvement in value-based care contract performance.
          The base case is auditable directly from these inputs:{' '}
          <strong>{CARE_COORD_VALUE_CHAIN}</strong> The conservative and aggressive scenarios run the
          same chain with the reduction rate, contract uplift and adoption ramp at their scenario
          values.
        </p>
        <AssumptionTable rows={REVENUE_ASSUMPTIONS} />
      </>
    ),
  },
  {
    title: 'Cost Structure Assumptions',
    content: (
      <>
        <p>
          These assumptions inform the investment presented in the CFO ROI Summary. The schedule is{' '}
          <strong>{CANON.investmentSchedule}</strong>, which over the {ROI_CONFIG.horizonYears}-year
          horizon totals <strong>{CANON.totalCost3Yr}</strong>. Note that this is a TOTAL cost, not a
          Year-1 implementation figure: Year 1 carries{' '}
          {formatUsd(CARE_COORD_ROI.scenarios.base.year1Cost)} of it.
        </p>
        <AssumptionTable rows={COST_ASSUMPTIONS} />
      </>
    ),
  },
  {
    title: 'Benchmark Inputs',
    content: (
      <>
        <p>
          Benchmark inputs were derived from industry care coordination performance data
          and internal health system operational baselines. External sources were selected
          based on validated relevance to inpatient transition management programs at
          integrated delivery networks of comparable scale.
        </p>
        <BenchmarkTable />
      </>
    ),
  },
  {
    title: 'Calculation Method',
    content: (
      <>
        <p>
          The inputs above are consumed by one financial model. These are its definitions — the same
          ones disclosed on the ROI Model screen and in the CFO ROI Summary. Each output figure in
          any of those documents can be recomputed from the input tables using only these rules.
        </p>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={{ ...thStyle, width: '22%' }}>Term</th>
              <th style={thStyle}>Definition</th>
            </tr>
          </thead>
          <tbody>
            {CARE_COORD_DISCLOSURE.map((line, i) => (
              <tr key={line.term} style={i % 2 === 0 ? rowEven : rowOdd}>
                <td style={tdLabelStyle}>{line.term}</td>
                <td style={tdStyle}>{line.definition}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p style={{ marginTop: '10px', fontSize: '8pt', color: '#717182', fontStyle: 'italic' }}>
          Sensitivity ranking (by effect on net {ROI_CONFIG.horizonYears}-year value):{' '}
          {CARE_COORD_ROI.sensitivity
            .slice(0, 3)
            .map(
              (row, i) =>
                `${i + 1}. ${row.name.replace(/\s*\([^)]*\)\s*$/, '')} (±${formatUsd(row.span / 2)})`,
            )
            .join(' · ')}
          . These are the inputs a finance review should validate first.
        </p>
      </>
    ),
  },
  {
    title: 'Confidence Assessment',
    content: (
      <>
        <p>
          The model carries a <strong>{CANON.confidencePct} confidence score</strong>, and it is
          computed rather than asserted — {CANON_CONFIDENCE_BASIS}. Concretely: each input above is
          scored by the strength of its source ({Math.round(ROI_CONFIG.confidenceWeights.High * 100)}%
          for a benchmark, {Math.round(ROI_CONFIG.confidenceWeights.Medium * 100)}% for an internal
          estimate), those scores are weighted by how much each input moves net 3-year value in the
          sensitivity analysis, and the result is discounted by up to{' '}
          {Math.round(ROI_CONFIG.upsideConfidenceDiscount * 100)}% for any input set above its
          benchmark value. This is the same number, from the same rule, that appears on the ROI Model
          screen, in the CFO ROI Summary, and in the Execute Command Center.
        </p>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '10px',
          marginTop: '12px',
        }}>
          {[
            { label: 'Supporting Factors',   color: '#059669', items: ['Internal readmission and discharge volume data available', 'Existing care management and analytics infrastructure in place', 'Multiple confirmed external benchmarks available', 'Value-based contract exposure modeled using representative shared savings terms'] },
            { label: 'Limiting Factors',      color: '#D97706', items: ['EHR integration completeness not yet confirmed', 'Care management capacity assumptions based on target state', 'Value-based contract improvement subject to payer negotiation', 'Phase 1 cohort coverage depends on workflow adoption pace'] },
          ].map((col) => (
            <div key={col.label} style={{
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              padding: '12px 14px',
              background: '#ffffff',
            }}>
              <div style={{
                fontSize: '7pt',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: col.color,
                marginBottom: '8px',
                paddingBottom: '6px',
                borderBottom: '1px solid #e5e7eb',
              }}>
                {col.label}
              </div>
              <ul style={{ paddingLeft: '14px', margin: 0 }}>
                {col.items.map((item, i) => (
                  <li key={i} style={{ fontSize: '8.5pt', color: '#374151', lineHeight: 1.6, marginBottom: '4px' }}>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div style={{
          marginTop: '14px',
          background: '#F5F7FA',
          borderLeft: '4px solid #18365E',
          borderRadius: '4px',
          padding: '12px 16px',
          fontSize: '8.5pt',
          color: '#18365E',
          fontWeight: 500,
          lineHeight: 1.6,
        }}>
          <strong>Model Validation Note:</strong> These assumptions should be reviewed
          and confirmed with clinical informatics, care management leadership, and finance
          prior to final capital approval. The model is designed to be updated as
          organizational data becomes available during Phase 0 scoping.
        </div>
      </>
    ),
  },
];

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function EAPrintableAssumptionsAppendixPage() {
  return (
    <EAUseCaseArtifactShell
      title="Assumptions Appendix"
      subtitle="Financial Model Inputs & Benchmarks"
      clientName={DEMO_CLIENT.name}
      useCaseName="Care Coordination and Transition Management"
      reportDate={demoMonthYear()}
      summaryMetrics={SUMMARY_METRICS}
      sections={SECTIONS}
      manualPrint
      printFilename="Assumptions Appendix — Care Coordination and Transition Management"
      printLabel="Assumptions Appendix — Print Preview"
    />
  );
}